import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Button, Form, Input, InputNumber, message, Modal, Select } from 'antd';
import {
  createPetLifeStageApi,
  deletePetLifeStageApi,
  getPetLifeStageDetailApi,
  getPetLifeStageListApi,
  updatePetLifeStageApi,
} from '@/api/petManage/lifeStage';
import './index.scss';
import { checkPermissions } from '@/utils/utils';
import { PET_LIFE_STAGE_KEY } from '@/router/Permissions';

const petTypeLabel = (type?: string) => {
  if (type === '1') return '狗';
  if (type === '2') return '猫';
  return type || '';
};

const bodySizeLabelMap: Record<string, string> = {
  '': '全部',
  '4': '迷你型犬',
  '1': '小型犬',
  '2': '中型犬',
  '3': '大型犬',
  '5': '巨型犬',
};
const bodySizeDisplayOrder = ['4', '1', '2', '3', '5'];
const bodySizeOptions = bodySizeDisplayOrder.map(value => ({ label: bodySizeLabelMap[value], value }));
const petTypeOptions = [
  { label: '狗', value: '1' },
  { label: '猫', value: '2' },
];

const formatMonths = (min?: number | null, max?: number | null) => {
  if (min == null && max == null) return '月龄范围：--';
  const start = min ?? 0;
  const end = max ?? '以上';
  return `月龄范围：${start} - ${end}${typeof end === 'number' ? '月龄' : ''}`;
};

const LifeStageManage: React.FC = () => {
  const [lifeStages, setLifeStages] = useState<any[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [currentStage, setCurrentStage] = useState<any | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('edit');
  const [form] = Form.useForm();
  const canEdit = useMemo(() => checkPermissions(PET_LIFE_STAGE_KEY.EDIT), []);

  const fetchLifeStages = useCallback(async () => {
    try {
      const res = await getPetLifeStageListApi();
      if (res.code === 200) {
        setLifeStages(res.data || res.rows || []);
      }
    } catch (error) {
      console.error('获取生命阶段失败', error);
    }
  }, []);

  useEffect(() => {
    fetchLifeStages();
  }, [fetchLifeStages]);

  const groupedData = useMemo(() => {
    const grouped: Record<string, Record<string, any[]>> = {};
    lifeStages.forEach(item => {
      const typeKey = item.petType || 'other';
      if (!grouped[typeKey]) grouped[typeKey] = {};
      const bodyKey = item.bodySize ?? '';
      if (!grouped[typeKey][bodyKey]) grouped[typeKey][bodyKey] = [];
      grouped[typeKey][bodyKey].push(item);
    });
    Object.values(grouped).forEach(bodyMap => {
      Object.values(bodyMap).forEach(list => list.sort((a, b) => a.sort - b.sort));
    });
    return grouped;
  }, [lifeStages]);

  const handleAdd = () => {
    setModalMode('create');
    setCurrentStage(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = async (id: number) => {
    try {
      const res = await getPetLifeStageDetailApi(id);
      if (res.code === 200 && res.data) {
        setModalMode('edit');
        setCurrentStage(res.data);
        form.setFieldsValue({
          minMonths: res.data.minMonths ?? undefined,
          maxMonths: res.data.maxMonths ?? undefined,
          content: res.data.content || '',
        });
        setModalVisible(true);
      }
    } catch (error) {
      console.error('获取生命阶段详情失败', error);
    }
  };

  const handleSubmit = async () => {
    if (modalMode === 'edit' && !currentStage) return;
    try {
      const values = await form.validateFields();
      setModalLoading(true);
      let res;
      if (modalMode === 'edit' && currentStage) {
        const payload = {
          ...currentStage,
          ...values,
          id: currentStage.id,
        };
        res = await updatePetLifeStageApi(payload);
      } else {
        res = await createPetLifeStageApi(values);
      }
      if (res.code === 200) {
        message.success(modalMode === 'edit' ? '更新成功' : '新增成功');
        setModalVisible(false);
        form.resetFields();
        setCurrentStage(null);
        fetchLifeStages();
      }
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error('更新生命阶段失败', error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = (stage: any) => {
    Modal.confirm({
      title: '确认删除该生命阶段？',
      content: `${petTypeLabel(stage.petType)} - ${bodySizeLabelMap[stage.bodySize || ''] || ''} - ${stage.stageName}`,
      onOk: async () => {
        try {
          const res = await deletePetLifeStageApi(stage.id);
          if (res.code === 200) {
            message.success('删除成功');
            fetchLifeStages();
          }
        } catch (error) {
          console.error('删除生命阶段失败', error);
        }
      },
    });
  };

  return (
    <div className="life-stage-page">
      <Button
        hidden
        style={{ position: 'absolute', top: 15, right: 25 }}
        type="primary"
        onClick={handleAdd}>
        新增生命阶段
      </Button>

      {Object.keys(groupedData).map(petType => (
        <div
          className="pet-type-section"
          key={petType}>
          <div className="pet-type-section__title">{petTypeLabel(petType)}</div>
          {[
            ...bodySizeDisplayOrder.filter(key => groupedData[petType][key]),
            ...Object.keys(groupedData[petType]).filter(key => !bodySizeDisplayOrder.includes(key)),
          ].map(bodySizeKey => (
            <div
              className="body-size-block"
              key={bodySizeKey}>
              <div className="body-size-block__title">{bodySizeLabelMap[bodySizeKey] || ''}</div>
              <div className="life-stage-list">
                {groupedData[petType][bodySizeKey].map(stage => (
                  <div
                    key={stage.id}
                    className="life-stage-card">
                    <div className="life-stage-card__actions">
                      {canEdit && (
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEdit(stage.id)}></Button>
                      )}
                      <Button
                        hidden
                        type="text"
                        size="small"
                        danger
                        onClick={() => handleDelete(stage)}>
                        删除
                      </Button>
                    </div>
                    <div className="life-stage-card__title">{stage.stageName}</div>
                    <div className="life-stage-card__meta">{formatMonths(stage.minMonths, stage.maxMonths)}</div>
                    <div className="life-stage-card__meta">描述：{stage.content || '暂无描述'}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
      <Modal
        title={modalMode === 'edit' ? '编辑生命阶段' : '新增生命阶段'}
        open={modalVisible}
        confirmLoading={modalLoading}
        onCancel={() => {
          setModalVisible(false);
          setCurrentStage(null);
          form.resetFields();
        }}
        onOk={handleSubmit}>
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ style: { width: 120 } }}
          wrapperCol={{ style: { flex: 1 } }}>
          {modalMode === 'edit' ? (
            <>
              <Form.Item label="品种类型">
                <Input
                  value={petTypeLabel(currentStage?.petType)}
                  disabled
                />
              </Form.Item>
              <Form.Item label="体型">
                <Input
                  value={bodySizeLabelMap[currentStage?.bodySize || ''] || ''}
                  disabled
                />
              </Form.Item>
              <Form.Item label="阶段名称">
                <Input
                  value={currentStage?.stageName}
                  disabled
                />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                label="品种类型"
                name="petType"
                rules={[{ required: true, message: '请选择品种类型' }]}>
                <Select
                  placeholder="请选择"
                  options={petTypeOptions}
                />
              </Form.Item>
              <Form.Item
                label="体型"
                name="bodySize"
                rules={[{ required: true, message: '请选择体型' }]}>
                <Select
                  placeholder="请选择"
                  options={bodySizeOptions}
                />
              </Form.Item>
              <Form.Item
                label="阶段名称"
                name="stageName"
                rules={[{ required: true, message: '请输入阶段名称' }]}>
                <Input placeholder="请输入阶段名称" />
              </Form.Item>
            </>
          )}
          <Form.Item
            label="最小月龄"
            name="minMonths"
            rules={[{ required: true, message: '请输入最小月龄' }]}>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={999999999}
              precision={0}
              placeholder="请输入"
              addonAfter="月龄"
            />
          </Form.Item>
          <Form.Item
            label="最大月龄"
            name="maxMonths"
            rules={[{ required: true, message: '请输入最大月龄' }]}>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={999999999}
              precision={0}
              placeholder="请输入"
              addonAfter="月龄"
            />
          </Form.Item>
          <Form.Item
            label="提示文案"
            name="content"
            rules={[{ required: true, message: '请输入提示文案' }]}>
            <Input.TextArea
              rows={3}
              maxLength={22}
              placeholder="请输入"
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LifeStageManage;
